import { LocationSearch } from "@/components/location/LocationSearch.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  LocationDisplay,
  getLocationDisplayValue,
  serializeLocationFromSuggestion,
} from "@/components/ui/location-display.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import { useArtifactViewCtx } from "@/components/artifacts/view/ArtifactViewContext.tsx";
import type { ArtifactComponent } from "@/types/artifacts-view.ts";
import { MapPin, Pencil } from "lucide-react";
import { useEffect, useRef, useState } from "react";

type LocationComponentData = Extract<ArtifactComponent, { type: "location" }>;

export const LocationBlock = ({ component }: { component: LocationComponentData }) => {
  const { isReadOnly, patchOne } = useArtifactViewCtx();
  const canEdit = !isReadOnly && component.editable;

  const [editing, setEditing] = useState(false);
  // Split state: `inputValue` is the live text shown in the field while
  // `valueToSave` holds the committed value, so an in-progress edit doesn't
  // clobber the saved location until the user confirms.
  const [inputValue, setInputValue] = useState(getLocationDisplayValue(component.value));
  const [valueToSave, setValueToSave] = useState(component.value);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setInputValue(getLocationDisplayValue(component.value));
    setValueToSave(component.value);
  }, [component.value]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
    }
  }, [editing]);

  const save = async () => {
    try {
      await patchOne(component.editPath, valueToSave);
      setEditing(false);
    } catch (error) {
      console.error(error);
      errorToast("Error updating location");
    }
  };

  const cancel = () => {
    setInputValue(getLocationDisplayValue(component.value));
    setValueToSave(component.value);
    setEditing(false);
  };

  return (
    <div className={`flex flex-row gap-2 ${editing ? "items-center" : "items-start"}`}>
      <MapPin className={`size-5 ${editing ? "" : "mt-0.5"}`} />
      <div className="flex flex-col gap-2 flex-1">
        {editing ? (
          <LocationSearch
            value={inputValue}
            onChange={(v) => {
              setInputValue(v);
              setValueToSave(v);
            }}
            onLocationSelect={(loc) => {
              const serialized = serializeLocationFromSuggestion(loc);
              setInputValue(getLocationDisplayValue(serialized));
              setValueToSave(serialized);
            }}
            placeholder="Search for a location..."
            showSaveCancel
            onSave={save}
            onCancel={cancel}
            onDelete={() => {
              setInputValue("");
              setValueToSave("");
            }}
            originalValue={getLocationDisplayValue(component.value)}
            showCurrentLocation={false}
            showIcon={false}
            className="bg-background! border! border-input! rounded-md! h-10!"
            ref={inputRef}
          />
        ) : (
          <div className="flex flex-row items-center gap-2">
            {component.value ? (
              <LocationDisplay location={component.value} />
            ) : (
              <span className="text-sm text-muted-foreground">No location set</span>
            )}
            {canEdit ? (
              <Button
                onClick={() => setEditing(true)}
                variant="ghost"
                size="action"
                className="h-6 w-6 p-0"
                aria-label="Edit location"
                data-statsig-name="artifact-view-edit-location"
              >
                <Pencil className="size-3" />
              </Button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};
